import { html } from '../core/html.js';
import { t } from '../core/i18n.js';
import { formatNumber, languageName } from '../core/format.js';
import { inline } from './prose.js';

/**
 * Article figures. Data comes from the post's `figures.js` (shared by every
 * locale), words from the post's copy file. Bars and the layer grid are plain
 * HTML so labels wrap and stay readable at phone width; the line chart draws
 * only its strokes in SVG and keeps every label in HTML for the same reason.
 * Every figure carries a data table, so no value depends on colour or hover.
 *
 * Tones: `main` (blue), `alt` (terracotta), `third` (amber) and `muted` (grey);
 * the colours are tokens in `styles/components/blog.css`.
 */

const FORMATS = {
  percent: { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 },
  delta: { signDisplay: 'exceptZero', minimumFractionDigits: 3, maximumFractionDigits: 3 },
  integer: { maximumFractionDigits: 0 },
  decimal: { minimumFractionDigits: 3, maximumFractionDigits: 3 },
};

const format = (value, unit) => {
  const options = FORMATS[unit];
  if (!options) throw new Error(`Unknown figure unit: ${unit}`);
  return formatNumber(value, options);
};

const dataTable = (columns, rows) => html`
  <details class="viz__data">
    <summary>${t('blog.figure.data')}</summary>
    <div class="viz__table-wrap">
      <table class="viz__table">
        <thead><tr>${columns.map((column) => html`<th scope="col">${column}</th>`)}</tr></thead>
        <tbody>
          ${rows.map(
            ([head, ...cells]) => html`<tr><th scope="row">${head}</th>${cells.map((cell) => html`<td>${cell}</td>`)}</tr>`,
          )}
        </tbody>
      </table>
    </div>
  </details>
`;

const frame = (id, number, copy, body, table) => html`
  <figure class="viz" id="figure-${id}">
    <figcaption class="viz__caption">
      <span class="viz__label">${t('blog.figure.label', { number })}</span>
      <span class="viz__title">${copy.title}</span>
      <span class="viz__text">${inline(copy.caption)}</span>
    </figcaption>
    <div class="viz__body">${body}</div>
    ${table}
  </figure>
`;

const legend = (items) => html`
  <ul class="viz-legend">
    ${items.map(
      (item) => html`<li><span class="viz-legend__key is-${item.tone}" aria-hidden="true"></span>${item.label}</li>`,
    )}
  </ul>
`;

/* ── horizontal bars ─────────────────────────────────────────────────────── */

const barTone = (definition, row) => {
  if (row.tone) return row.tone;
  if (definition.noise != null) {
    if (Math.abs(row.value) <= definition.noise) return 'muted';
    return row.value < 0 ? 'main' : 'alt';
  }
  return 'main';
};

const bars = (definition, copy) => {
  const [min, max] = definition.domain;
  const at = (value) => (Math.min(Math.max(value, min), max) - min) / (max - min);
  const zero = at(0);
  const label = (row) => (definition.labels === 'language' ? languageName(row.key) : copy.rows[row.key]);
  const rows = definition.rows.map((row) => ({
    label: label(row),
    display: format(row.value, definition.unit),
    tone: barTone(definition, row),
    from: Math.min(zero, at(row.value)),
    to: Math.max(zero, at(row.value)),
    negative: row.value < 0,
  }));

  const body = html`
    <div class="${`viz-bars${definition.rows.length > 12 ? ' viz-bars--dense' : ''}${definition.reference != null ? ' viz-bars--ref' : ''}`}">
      <ol class="viz-bars__rows">
        ${rows.map(
          (row) => html`
            <li class="viz-bars__row">
              <span class="viz-bars__label">${row.label}</span>
              <span class="viz-bars__track">
                <span
                  class="${`viz-bars__bar is-${row.tone}${row.negative ? ' is-negative' : ''}`}"
                  style="--from: ${row.from.toFixed(4)}; --size: ${(row.to - row.from).toFixed(4)}"
                ></span>
              </span>
              <span class="viz-bars__value">${row.display}</span>
            </li>
          `,
        )}
      </ol>
      ${definition.noise != null && html`
        <span
          class="viz-bars__band"
          style="--from: ${at(-definition.noise).toFixed(4)}; --size: ${(at(definition.noise) - at(-definition.noise)).toFixed(4)}"
          aria-hidden="true"
        ></span>
      `}
      ${zero > 0 && html`<span class="viz-bars__ref" style="--at: ${zero.toFixed(4)}" aria-hidden="true"></span>`}
      ${definition.reference != null && html`
        <span class="viz-bars__ref is-labelled" style="--at: ${at(definition.reference).toFixed(4)}" aria-hidden="true">
          <span class="viz-bars__ref-label">${copy.reference}</span>
        </span>
      `}
    </div>
  `;

  const tableRows = rows.map((row) => [row.label, row.display]);
  if (definition.reference != null) tableRows.push([copy.reference, format(definition.reference, definition.unit)]);
  return { body, table: dataTable(copy.columns, tableRows) };
};

/* ── layer grid ──────────────────────────────────────────────────────────── */

const LAYER_TONES = { local: 'main', full: 'alt', linear: 'third' };

const layers = (definition, copy) => {
  const kind = (layer, head) => {
    if (head < definition.local) return 'local';
    return layer % definition.fullEvery === 0 ? 'full' : 'linear';
  };
  const layerIndexes = Array.from({ length: definition.layers }, (_, index) => index);
  // Extra heads on top, so the alternation between global and linear reads first.
  const headRows = Array.from({ length: definition.local + definition.extra }, (_, index) => definition.local + definition.extra - 1 - index);
  const layersOf = (wanted) =>
    layerIndexes.filter((layer) => headRows.some((head) => kind(layer, head) === wanted)).map((layer) => layer + 1);
  const listOf = (numbers) =>
    numbers.length === definition.layers ? `1–${definition.layers}` : numbers.map((n) => formatNumber(n)).join(', ');

  const body = html`
    <div class="viz-layers">
      ${legend(Object.entries(LAYER_TONES).map(([key, tone]) => ({ tone, label: copy.legend[key] })))}
      <div class="viz-layers__frame" dir="ltr" aria-hidden="true">
        <div class="viz-layers__grid" style="--layers: ${definition.layers}">
          ${headRows.map((head) =>
            layerIndexes.map((layer) => html`<span class="viz-layers__cell is-${LAYER_TONES[kind(layer, head)]}"></span>`),
          )}
        </div>
        <div class="viz-layers__axis" style="--layers: ${definition.layers}">
          ${layerIndexes.map((layer) => html`<span>${formatNumber(layer + 1)}</span>`)}
        </div>
      </div>
      <p class="viz-layers__axis-label" aria-hidden="true">${copy.axis}</p>
    </div>
  `;

  const table = dataTable(copy.columns, [
    [copy.legend.local, listOf(layersOf('local')), formatNumber(definition.local)],
    [copy.legend.full, listOf(layersOf('full')), formatNumber(definition.extra)],
    [copy.legend.linear, listOf(layersOf('linear')), formatNumber(definition.extra)],
  ]);
  return { body, table };
};

/* ── line chart ──────────────────────────────────────────────────────────── */

const line = (definition, copy) => {
  const { x, y } = definition;
  const px = (value) => (value - x.min) / (x.max - x.min);
  const py = (value) => 1 - (value - y.min) / (y.max - y.min);
  const path = (points) =>
    points.map(([a, b], index) => `${index ? 'L' : 'M'}${(px(a) * 1000).toFixed(1)} ${(py(b) * 1000).toFixed(1)}`).join('');
  const series = definition.series.map((entry) => ({ ...entry, name: copy.series[entry.key] }));
  const payload = {
    x: { min: x.min, max: x.max, unit: copy.xUnit },
    y: { min: y.min, max: y.max, decimals: y.decimals },
    series: series.map((entry) => ({ name: entry.name, tone: entry.tone, points: entry.points })),
  };

  const body = html`
    <div class="viz-line" data-viz-line="${JSON.stringify(payload)}">
      ${legend(series.map((entry) => ({ tone: entry.tone, label: entry.name })))}
      <div class="viz-line__frame" dir="ltr">
        <p class="viz-line__ylabel">${copy.yLabel}</p>
        <div class="viz-line__plot" tabindex="0" role="img" aria-label="${copy.title}">
          ${y.ticks.map(
            (value) => html`
              <span class="viz-line__grid" style="--y: ${py(value).toFixed(4)}">
                <span class="viz-line__tick">${formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
              </span>
            `,
          )}
          ${definition.events.map(
            (event, index) => html`
              <span
                class="${`viz-line__event${px(event.at) > 0.6 ? ' is-end' : ''}`}"
                style="--x: ${px(event.at).toFixed(4)}; --row: ${index % 2}"
              >
                <span class="viz-line__event-label">${copy.events[event.key]}</span>
              </span>
            `,
          )}
          <svg class="viz-line__svg" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            ${series.map(
              (entry) => html`<path class="viz-line__path is-${entry.tone}" d="${path(entry.points)}" vector-effect="non-scaling-stroke"></path>`,
            )}
          </svg>
          ${series.map((entry) => {
            const [a, b] = entry.points[entry.points.length - 1];
            return html`<span class="viz-line__dot is-${entry.tone}" style="--x: ${px(a).toFixed(4)}; --y: ${py(b).toFixed(4)}" aria-hidden="true"></span>`;
          })}
          <span class="viz-line__cursor" hidden></span>
          <span class="viz-line__tip" hidden aria-hidden="true"></span>
        </div>
        <div class="viz-line__xaxis" aria-hidden="true">
          ${x.ticks.map(
            (value) => html`<span style="--x: ${px(value).toFixed(4)}">${formatNumber(value, { maximumFractionDigits: 1 })}</span>`,
          )}
        </div>
        <p class="viz-line__xlabel">${copy.xLabel}</p>
      </div>
    </div>
  `;

  // The table follows the held-out series, the first one, at its own points.
  const [lead, ...rest] = series;
  const nearest = (points, at) =>
    points.reduce((best, point) => (Math.abs(point[0] - at) < Math.abs(best[0] - at) ? point : best));
  const decimals = { minimumFractionDigits: y.decimals, maximumFractionDigits: y.decimals };
  const tableRows = lead.points
    .filter((point, index) => index % definition.tableEvery === 0 || index === lead.points.length - 1)
    .map(([at, value]) => [
      formatNumber(at, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      formatNumber(value, decimals),
      ...rest.map((entry) => formatNumber(nearest(entry.points, at)[1], decimals)),
    ]);
  return { body, table: dataTable([copy.xLabel, ...series.map((entry) => entry.name)], tableRows) };
};

const KINDS = { bars, layers, line };

/** One numbered figure: caption, chart and its data table. */
export const vizFigure = (id, number, definition, copy) => {
  const build = KINDS[definition.type];
  if (!build) throw new Error(`Unknown figure type: ${definition.type}`);
  if (!copy) throw new Error(`Figure "${id}" has no copy`);
  const { body, table } = build(definition, copy);
  return frame(id, number, copy, body, table);
};
